import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ExerciseScheduleForm from "./ExerciseScheduleForm";
import { createExerciseSchedule } from "../../store/slices/exerciseSchedulesSlice";

const AddExerciseSchedule = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState({
        caseId: caseId || null,
        patientId: patientId || null,
        weight: "",
        bmi: "",
        goal: "",
        plan: "",
        clientName: "",
        responsibleStaff: "",
        scheduleRows: [
            {
                date: "",
                time: "",
                weeklyOutcome: "",
                staffName: "",
                physicalActivities: {},
                gymExercises: {}
            }
        ],
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addRow = () => {
        setFormData(prev => ({
            ...prev,
            scheduleRows: [
                ...prev.scheduleRows,
                {
                    date: "",
                    time: "",
                    weeklyOutcome: "",
                    staffName: "",
                    physicalActivities: {},
                    gymExercises: {}
                }
            ]
        }));
    };

    const removeRow = (index) => {
        setFormData(prev => ({
            ...prev,
            scheduleRows: prev.scheduleRows.filter((_, i) => i !== index)
        }));
    };

    // Helper function to convert camelCase to snake_case
    const toSnakeCase = (str) => {
        return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    // Convert object keys from camelCase to snake_case
    const convertToSnakeCase = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(item => convertToSnakeCase(item));
        } else if (obj !== null && typeof obj === 'object') {
            const newObj = {};
            Object.keys(obj).forEach(key => {
                const snakeKey = toSnakeCase(key);
                newObj[snakeKey] = convertToSnakeCase(obj[key]);
            });
            return newObj;
        }
        return obj;
    };

    // Prepare the payload for API
    const preparePayload = () => {
        // Convert scheduleRows to the format expected by backend
        const rows = formData.scheduleRows.map(row => {
            // Map physicalActivities to backend fields
            const physicalActivities = row.physicalActivities || {};

            return {
                date: row.date || null,
                time: row.time || null,
                weekly_outcome: row.weeklyOutcome || null,
                staff_name: row.staffName || null,

                // Physical activities mapping
                phys_stretching: physicalActivities["Stretching Exercise"]?.done || null,
                phys_stretching_duration: physicalActivities["Stretching Exercise"]?.done === "yes"
                    ? physicalActivities["Stretching Exercise"]?.duration || null
                    : null,
                phys_stretching_reason: physicalActivities["Stretching Exercise"]?.done === "no"
                    ? physicalActivities["Stretching Exercise"]?.reason || null
                    : null,

                phys_walking: physicalActivities["Walking"]?.done || null,
                phys_walking_duration: physicalActivities["Walking"]?.done === "yes"
                    ? physicalActivities["Walking"]?.duration || null
                    : null,
                phys_walking_reason: physicalActivities["Walking"]?.done === "no"
                    ? physicalActivities["Walking"]?.reason || null
                    : null,

                phys_strengthening: physicalActivities["Strengthening Exercise"]?.done || null,
                phys_strengthening_duration: physicalActivities["Strengthening Exercise"]?.done === "yes"
                    ? physicalActivities["Strengthening Exercise"]?.duration || null
                    : null,
                phys_strengthening_reason: physicalActivities["Strengthening Exercise"]?.done === "no"
                    ? physicalActivities["Strengthening Exercise"]?.reason || null
                    : null,

                // Gym exercises mapping
                gym_tread: row.gymExercises?.["Tread Mill"]?.done || null,
                gym_tread_duration: row.gymExercises?.["Tread Mill"]?.done === "yes"
                    ? row.gymExercises["Tread Mill"]?.duration || null
                    : null,
                gym_tread_reason: row.gymExercises?.["Tread Mill"]?.done === "no"
                    ? row.gymExercises["Tread Mill"]?.reason || null
                    : null,

                gym_bicycle: row.gymExercises?.["Bi-Cycle"]?.done || null,
                gym_bicycle_duration: row.gymExercises?.["Bi-Cycle"]?.done === "yes"
                    ? row.gymExercises["Bi-Cycle"]?.duration || null
                    : null,
                gym_bicycle_reason: row.gymExercises?.["Bi-Cycle"]?.done === "no"
                    ? row.gymExercises["Bi-Cycle"]?.reason || null
                    : null,

                gym_anysport: row.gymExercises?.["Any Sport Activity"]?.done || null,
                gym_anysport_duration: row.gymExercises?.["Any Sport Activity"]?.done === "yes"
                    ? row.gymExercises["Any Sport Activity"]?.duration || null
                    : null,
                gym_anysport_reason: row.gymExercises?.["Any Sport Activity"]?.done === "no"
                    ? row.gymExercises["Any Sport Activity"]?.reason || null
                    : null
            };
        });

        // Main payload
        const payload = {
            case_id: formData.caseId,
            patient_id: formData.patientId,
            weight: formData.weight || null,
            bmi: formData.bmi || null,
            goal: formData.goal || null,
            plan: formData.plan || null,
            client_name: formData.clientName || null,
            responsible_staff: formData.responsibleStaff || null,
            document_name: "Weekly Clients Exercise Schedule",
            effective_date: formData.effectiveDate || null,
            approved_by: formData.approvedBy || null,
            approved_date: formData.approvedDate || null,
            next_revision_by: formData.nextRevisionBy || null,
            next_revision_date: formData.nextRevisionDate || null,
            rows: rows
        };

        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.clientName || !formData.responsibleStaff) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in Client Name and Responsible Staff",
                icon: "warning",
            });
            return;
        }

        if (formData.scheduleRows.length === 0) {
            Swal.fire({
                title: "Missing Data",
                text: "Please add at least one schedule row",
                icon: "warning",
            });
            return;
        }

        setIsLoading(true);

        try {
            const payload = preparePayload();

            const res = await dispatch(createExerciseSchedule(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Weekly Exercise Schedule has been created successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/exercise-schedule/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (error) {
            console.error("Error creating exercise schedule:", error);

            let errorMessage = "Failed to create exercise schedule";
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.errors) {
                // Handle validation errors
                const errorMessages = Object.values(error.errors)
                    .flat()
                    .join("\n");
                errorMessage = errorMessages;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ExerciseScheduleForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                addRow={addRow}
                                removeRow={removeRow}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-2-line me-1"></i> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddExerciseSchedule;